/**
 * THE STAGES A PROJECT RUNS IN — #377.
 *
 * A phase is a stage of the work (Discovery → Build → Test → Cutover). It is the
 * project's shape over time: seeded from the project type at creation, then the
 * board's to reshape. This is the app-side vocabulary for the `phases` table in
 * migration 0092; the seam in src/backend/paths.ts translates the two.
 *
 * A phase is deliberately close to an Area (#269) in shape — an ordered,
 * per-board, retire-never-delete row — because the same arguments apply. What it
 * adds is a `status` and a `targetEnd`, because a phase has a life and a
 * deadline where an area is only a label.
 */

/**
 * WHERE A STAGE IS IN ITS LIFE. Three words the product owns (so the column is a
 * check constraint), unlike the name, which is the customer's.
 */
import type { IssueStatus } from './types';
import type { GoLiveAnchor } from './projects';

export type PhaseStatus = 'planned' | 'active' | 'done';

export const PHASE_STATUSES: PhaseStatus[] = ['planned', 'active', 'done'];

export const PHASE_STATUS_LABELS: Record<PhaseStatus, string> = {
  planned: 'Planned',
  active: 'Active',
  done: 'Done',
};

export interface Phase {
  id: string;
  name: string;
  /** The order the stages run in, chosen by whoever set them up. */
  position: number;
  status: PhaseStatus;
  /** The person who leads the stage — a member email, or '' when unnamed. #377. */
  owner?: string;
  /** The day the stage begins, `YYYY-MM-DD`. With targetEnd it gives the stage a
   *  window to read "behind by N days" against. A plain day, like targetEnd. */
  startsOn?: string | null;
  /**
   * The day the stage is meant to be finished by, as `YYYY-MM-DD`, or absent/null
   * when nobody has said. A plain day rather than a millisecond moment — like a
   * sprint's dates, so a target read in one timezone is the same day in another.
   * Phases run in sequence, so a phase's window is [the previous target_end (or
   * the project's start), this target_end]; that window is what #376 measures
   * lateness against and #384 accrues cost over.
   */
  targetEnd?: string | null;
  /**
   * WHEN IT ACTUALLY FINISHED — the instant it entered done, or absent while it
   * has not. The other half of "on time?": target_end is the plan, this is what
   * happened. Written by the database on every completion path and cleared if the
   * phase is reopened (migration 0097); the app only reads it.
   */
  doneAt?: number | null;
  /** When it was retired; absent or null means still live. Retiring is "remove". */
  retiredAt?: number | null;
  /** Whether this phase's end is a committee-grade MILESTONE — 0124. A promoted
   *  phase end shows on the portfolio milestones timeline with no second entry. */
  isMilestone?: boolean;
  /** The label the milestone reads as, when the phase name is not what the
   *  committee calls the moment (e.g. phase "Build" → milestone "Feature freeze"). */
  milestoneLabel?: string | null;
}

/** Only the stages still live, in order. Retired ones stay readable elsewhere. */
export function livePhases(phases: Phase[]): Phase[] {
  return phases.filter((p) => !p.retiredAt).sort((a, b) => a.position - b.position);
}

/**
 * "This card is in no phase" as a filter value — the same rule areas already
 * state with FILTER_NO_AREA. Most cards sit in no phase, so it is the largest
 * single answer and not an edge case; it cannot be an empty string, because an
 * empty filter LIST already means "every phase".
 */
export const FILTER_NO_PHASE = '(no phase)';

/* ── A phase is WORKED, step by step, and SIGNED OFF — #377, the Plan view ──── */

/** Where the WORK on a step stands. The sign-off is a separate question the
 *  signers answer: a step can be `done` and still awaiting a signature. */
export type StepStatus = 'todo' | 'doing' | 'done' | 'blocked';

/** One person who has to say yes to a step, and whether they have. `signedAt`
 *  is an ISO instant once they sign, null/absent while it is still awaited. */
export interface StepSigner {
  person: string;
  role: string;
  signedAt?: string | null;
}

/** A fluid unit of work inside a phase, added as the work uncovers it. */
export interface Step {
  id: string;
  phaseId: string;
  name: string;
  assignee?: string;
  due?: string | null;
  status: StepStatus;
  blockedReason?: string;
  signers: StepSigner[];
  position: number;
  addedAt?: number | null;
  retiredAt?: number | null;
}

/** One line of a phase's append-only history. */
export interface PhaseEvent {
  id: string;
  phaseId: string;
  actor: string;
  kind: string;
  summary: string;
  at: number;
}

/** The live steps of one phase, in order. */
export function stepsOf(steps: Step[], phaseId: string): Step[] {
  return steps
    .filter((s) => s.phaseId === phaseId && !s.retiredAt)
    .sort((a, b) => a.position - b.position);
}

/** How many of a step's signers have signed, and how many there are. */
export function signTally(step: Step): { signed: number; total: number } {
  const signers = step.signers ?? [];
  return { signed: signers.filter((s) => !!s.signedAt).length, total: signers.length };
}

/** A step is signed off when it has signers and every one has signed. A step with
 *  nobody assigned to sign is NOT signed off — it is waiting to be assigned. */
export function stepSignedOff(step: Step): boolean {
  const { signed, total } = signTally(step);
  return total > 0 && signed === total;
}

/** A phase can be signed off once it has steps and all of them are signed off —
 *  the gate the "Sign off phase" button reads. */
export function phaseSignable(steps: Step[], phaseId: string): boolean {
  const live = stepsOf(steps, phaseId);
  return live.length > 0 && live.every(stepSignedOff);
}

/** Share of a phase's steps whose work is done, 0–100 — the progress bar. */
export function phaseProgress(steps: Step[], phaseId: string): number {
  const live = stepsOf(steps, phaseId);
  if (!live.length) return 0;
  return Math.round((live.filter((s) => s.status === 'done').length / live.length) * 100);
}

/** Whole days between a `YYYY-MM-DD` day and today, read at local midnight so a
 *  timezone cannot shift the count by one. Positive means the day is in the past. */
export function daysPast(day: string | null | undefined, nowMs = Date.now()): number {
  if (!day) return 0;
  const [y, m, d] = day.split('-').map(Number);
  if (!y || !m || !d) return 0;
  const then = new Date(y, m - 1, d).getTime();
  const today = new Date(new Date(nowMs).getFullYear(), new Date(nowMs).getMonth(), new Date(nowMs).getDate()).getTime();
  return Math.round((today - then) / 86_400_000);
}

/** A millisecond instant as its local `YYYY-MM-DD` day — so an actual finish
 *  (doneAt, ms) can be compared against a plain target day like the go-live. */
export function msToDay(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** How many days before the go-live the app starts warning that an unfinished
 *  phase is running out of runway. The go-live is ONE hard date, so this is a
 *  fixed window — not the board-relative percentile the card-aging marker uses. */
export const GO_LIVE_APPROACHING_DAYS = 14;

/**
 * THE PLANNED GO-LIVE DAY, RESOLVED FROM ITS ANCHOR (0217). The go-live can be anchored to
 * the plan instead of a free-typed date, so its planned day is DERIVED:
 *   · `phase`      → the end of that phase (`targetEnd`) — moving the phase moves the go-live,
 *                    so it can never silently contradict the plan.
 *   · `milestone`  → that milestone's planned day (looked up by the caller via `milestoneDay`,
 *                    which knows the milestone register; absent lookup → falls through).
 *   · `fixed`/none → the typed `goLive`, kept as a committed date.
 * Returns a plain `YYYY-MM-DD` day, or undefined when there is nothing to anchor to and no
 * typed date. A phase/milestone anchor whose target is missing or undated falls back to the
 * typed date, so the header never goes blank mid-edit. Pure — the one place the anchor is read.
 */
export function goLiveAnchorDay(
  project: { goLive?: number; goLiveAnchor?: GoLiveAnchor },
  phases: readonly Pick<Phase, 'id' | 'targetEnd'>[],
  milestoneDay?: (id: string) => string | null | undefined,
): string | undefined {
  const a = project.goLiveAnchor;
  if (a?.kind === 'phase' && a.id) {
    const ph = phases.find((p) => p.id === a.id);
    if (ph?.targetEnd) return ph.targetEnd;
  } else if (a?.kind === 'milestone' && a.id && milestoneDay) {
    const d = milestoneDay(a.id);
    if (d) return d;
  }
  return project.goLive != null ? new Date(project.goLive).toISOString().slice(0, 10) : undefined;
}

/**
 * THE GO-LIVE TARGET AS A LOCAL-MIDNIGHT INSTANT (ms), or null when no date is set — the one
 * place the forecast panel and the per-card "will it make the release?" odds read the target,
 * so the two can never land on different days. It resolves the anchor exactly like
 * `goLiveAnchorDay` (phase end → milestone day → typed date), then places that calendar day at
 * LOCAL midnight — the same axis the Monte-Carlo percentiles live on, so a day parsed as UTC
 * midnight can't read a day early west of Greenwich (the planMs lesson, src/milestone-forecast.ts).
 */
export function goLiveTargetMs(
  project: { goLive?: number; goLiveAnchor?: GoLiveAnchor } | null | undefined,
  phases: readonly Pick<Phase, 'id' | 'targetEnd'>[],
  milestoneDay?: (id: string) => string | null | undefined,
): number | null {
  if (!project) return null;
  const day = goLiveAnchorDay(project, phases, milestoneDay);
  if (!day) return null;
  const [y, mo, d] = day.split('-').map(Number);
  if (!y || !mo || !d) return null;
  return new Date(y, mo - 1, d).getTime();
}

/** Whether the go-live is anchored to the WORK (a phase or milestone), so its date is derived
 *  and a phase ending after it is legitimate post-launch work — not a plan breach (0217). A
 *  `fixed` or absent anchor is NOT plan-anchored: the typed date is held and still breaches. */
export function goLiveIsPlanAnchored(anchor: GoLiveAnchor | undefined): boolean {
  return anchor?.kind === 'phase' || anchor?.kind === 'milestone';
}

/**
 * WHERE A PHASE STANDS AGAINST THE GO-LIVE — the one derived answer the Plan, the
 * status report and the AAR all read. It replaces the old stored-status trio
 * (goLiveAtRisk / daysBehind / phaseHealth), which was rendered nowhere and read
 * the UNTRUSTED stored status. This is fed the status the Plan actually SHOWS
 * (the card-derived status, or a manual override), so it can never disagree with
 * the row it sits on, and it is the same reading the critical-path "float to
 * go-live" uses — one computation, several renderings.
 *
 *  clear          nothing to say
 *  approaching    not finished, and the go-live is within GO_LIVE_APPROACHING_DAYS
 *  planned-breach not finished, and this phase is PLANNED to end after go-live — only when the
 *                 go-live is a FIXED/committed date, not when the plan is what sets the go-live
 *  post-go-live   not finished, and this phase is planned to end AFTER a PLAN-ANCHORED go-live:
 *                 legitimate hypercare / post-launch work, shown calm, never a breach (0217)
 *  past-open      not finished, and the go-live day has already passed (the alarm)
 *  slipped        finished, but it finished AFTER the go-live (a recorded slip)
 */
export type GoLiveState = 'clear' | 'approaching' | 'planned-breach' | 'post-go-live' | 'past-open' | 'slipped';

export function goLiveState(
  phase: Phase,
  goLiveDay: string | null | undefined,
  shownStatus: PhaseStatus,
  nowMs = Date.now(),
  /** True when the go-live is anchored to the plan (a phase/milestone), so a phase ending after
   *  it is calm post-launch work rather than a breach (0217). Defaults to false = the old
   *  fixed-date behaviour, so every existing caller reads exactly as before. */
  planAnchored = false,
): GoLiveState {
  if (!goLiveDay) return 'clear';
  // A phase PLANNED to end after a plan-anchored go-live is post-launch work by design — calm,
  // whether it is still open or already finished (so no false "slipped" either).
  const endsAfterGoLive = !!phase.targetEnd && phase.targetEnd > goLiveDay;
  const isPostGoLive = planAnchored && endsAfterGoLive;
  if (shownStatus === 'done') {
    if (!isPostGoLive && phase.doneAt != null && msToDay(phase.doneAt) > goLiveDay) return 'slipped';
    return 'clear';
  }
  if (isPostGoLive) return 'post-go-live';
  // Not finished from here. `daysPast` is >0 once the go-live day is behind us.
  const past = daysPast(goLiveDay, nowMs);
  if (past > 0) return 'past-open';
  // Plain YYYY-MM-DD strings compare correctly by day.
  if (endsAfterGoLive) return 'planned-breach';
  if (past >= -GO_LIVE_APPROACHING_DAYS) return 'approaching';
  return 'clear';
}

/** How near a target date has to be before a card reads "due soon". A small
 *  fixed window, like GO_LIVE_APPROACHING_DAYS — a card's own deadline, not a
 *  board-relative percentile. */
export const DUE_SOON_DAYS = 3;

/**
 * WHERE A CARD STANDS AGAINST ITS OWN TARGET DATE (Track B) — the counterpart to
 * goLiveState above, for the per-card due date. Derived, never stored, so it can
 * never disagree with the date on the card. A RESOLVED card is always clear: a
 * finished card is never overdue, however late it was.
 *
 *  clear      no target date, or comfortably ahead of it (or the card is done)
 *  due-soon   the target is today or within DUE_SOON_DAYS
 *  overdue    the target day has passed and the card is not finished
 */
export type CardDueState = 'clear' | 'due-soon' | 'overdue';

export function cardDueState(
  due: string | null | undefined,
  status: IssueStatus,
  nowMs = Date.now()
): CardDueState {
  if (!due) return 'clear';
  if (isResolved(status)) return 'clear';
  const past = daysPast(due, nowMs); // >0 once the day is behind us, 0 today, <0 ahead
  if (past > 0) return 'overdue';
  if (past >= -DUE_SOON_DAYS) return 'due-soon';
  return 'clear';
}

/**
 * THE STARTING PHASES FOR A KIND OF WORK — the seed the wizard offers and the
 * board can then reshape. Grounded in how the work actually runs, not a single
 * generic lifecycle stamped on everything: a data migration extracts and loads,
 * an ERP rollout configures and trains, a digitisation job scans and indexes.
 *
 * IT IS A SEED, NOT A CAGE. Every list here is editable the moment it lands, and
 * a custom or unrecognised type falls back to the plain project lifecycle rather
 * than being refused — the type only ever chooses where the board starts.
 */
const DEFAULT_PHASES = ['Initiation', 'Planning', 'Execution', 'Testing', 'Deployment', 'Closure'];

/** By group — most types share their group's shape. */
const GROUP_PHASES: Record<string, string[]> = {
  'Software & applications': ['Discovery', 'Design', 'Build', 'Testing', 'UAT', 'Deployment', 'Hypercare'],
  Data: ['Discovery', 'Data mapping', 'Build & transform', 'Validation', 'Cutover', 'Closure'],
  'Infrastructure & network': ['Assessment', 'Design', 'Procurement', 'Build', 'Testing', 'Cutover', 'Handover'],
  Cloud: ['Assessment', 'Design', 'Migration', 'Testing', 'Cutover', 'Optimisation'],
  'Security & compliance': ['Assessment', 'Gap analysis', 'Remediation', 'Validation', 'Audit', 'Closure'],
  'Enterprise systems': ['Discovery', 'Design', 'Configuration', 'Data migration', 'Testing', 'Training', 'Go-Live', 'Hypercare'],
  'Change & lifecycle': ['Assessment', 'Design', 'Implementation', 'Testing', 'Rollout', 'Closure'],
};

/** Where a type differs enough from its group to deserve its own shape. */
const TYPE_PHASES: Record<string, string[]> = {
  'Records digitisation': ['Preparation', 'Scanning', 'Indexing', 'Quality check', 'Handover'],
  'Application upgrade': ['Assessment', 'Planning', 'Upgrade', 'Testing', 'Cutover', 'Closure'],
  'System decommissioning': ['Assessment', 'Data archival', 'Shutdown', 'Verification', 'Closure'],
  'Hardware refresh': ['Assessment', 'Procurement', 'Deployment', 'Verification', 'Handover'],
  'Backup & disaster recovery': ['Assessment', 'Design', 'Implementation', 'DR testing', 'Handover'],
};

/**
 * Which group a type sits in, worked out once from the catalogue so this file and
 * the wizard's type list never drift. Imported lazily to avoid a circular import
 * with projects.ts, which pulls nothing from here.
 */
import { PROJECT_TYPE_GROUPS } from './projects';

const GROUP_OF_TYPE: Record<string, string> = Object.fromEntries(
  PROJECT_TYPE_GROUPS.flatMap((g) => g.types.map((t) => [t.toLowerCase(), g.group]))
);

/** The ordered phase names a project type starts with. */
export function phasesForType(projectType?: string): string[] {
  const key = (projectType ?? '').trim();
  if (!key) return [...DEFAULT_PHASES];
  const byType = TYPE_PHASES[key];
  if (byType) return [...byType];
  const group = GROUP_OF_TYPE[key.toLowerCase()];
  if (group && GROUP_PHASES[group]) return [...GROUP_PHASES[group]];
  return [...DEFAULT_PHASES];
}

/** A phase as the wizard collects it before the board exists — name plus, once
 *  dates are spread across the project, a target day. Status is always 'planned'
 *  at creation, so it is not carried here. */
export interface PhaseDraft {
  name: string;
  targetEnd?: string;
}

/** A local `YYYY-MM-DD`, read at local midnight so a western timezone does not
 *  push a target back a day. The inverse of the wizard's dayToMs. */
export function dayFromMs(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * The phases a project starts with, ready to write: the type's template, in
 * order, with target days SPREAD EVENLY across the project's start → go-live
 * span when both are known. Even spacing is a starting point a person adjusts,
 * not a claim about the plan — but it means a project created with dates has a
 * real schedule immediately, which is what #376 and #384 build on. With no
 * dates, the names land and the targets are filled in later.
 */
export function seedPhases(projectType?: string, startMs?: number, goLiveMs?: number): PhaseDraft[] {
  const names = phasesForType(projectType);
  if (typeof startMs !== 'number' || typeof goLiveMs !== 'number' || goLiveMs <= startMs) {
    return names.map((name) => ({ name }));
  }
  const span = goLiveMs - startMs;
  return names.map((name, i) => ({
    name,
    // The last phase lands exactly on go-live; the rest divide the span evenly.
    targetEnd: dayFromMs(startMs + Math.round((span * (i + 1)) / names.length)),
  }));
}

/**
 * What is wrong with a phase name, or '' if nothing is. A name is required and
 * two live phases on one board cannot share one (the database enforces the same
 * rule; this is so the screen says so before the write is refused).
 */
export function phaseProblem(name: string, existing: Phase[], selfId?: string): string {
  const trimmed = name.trim();
  if (!trimmed) return 'Give the phase a name.';
  const clash = livePhases(existing).some(
    (p) => p.id !== selfId && p.name.trim().toLowerCase() === trimmed.toLowerCase()
  );
  if (clash) return `There is already a phase called "${trimmed}".`;
  return '';
}

/* ── A PHASE READ THROUGH ITS CARDS — the Plan is a view over the board (D2) ───
 *
 * The Plan keeps no second copy of the work. A phase's progress and the status
 * it earns are read straight off the board cards that name it (an issue whose
 * `phase` is this phase's id), so a developer moving a card forward is what
 * advances the plan and the two can never drift apart. These take only the two
 * fields they read, so the Plan's rollups can be proved without a whole Issue.
 */

/** The little a rollup needs off a card: which phase it is in, and where its
 *  work stands. A real Issue satisfies this; so does a test fixture. */
export interface PhasedCard {
  phase?: string | null;
  status: IssueStatus;
  /** How far along the card's own work is, 0–100, when a person has set it — the
   *  granular contribution to the phase bar. Absent/null counts as 0 for an
   *  unresolved card; a resolved card always contributes 100 whatever this says. */
  progressPct?: number | null;
}

/** Work that is finished — delivered, or deliberately dropped. Both take a card
 *  out of "still to do", which is what a phase being done really means. */
function isResolved(s: IssueStatus): boolean {
  return s === 'done' || s === 'wontdo';
}
/** Work that has actually started — past the backlog and to-do columns. In
 *  Staging sits between In Progress and In Review, so it counts as started too. */
function isStarted(s: IssueStatus): boolean {
  return s === 'inprogress' || s === 'staging' || s === 'inreview' || s === 'done';
}

/**
 * How many cards run in a phase and how many are finished — the progress
 * fraction the header reads ("2 / 5") and the bar underneath it.
 *
 * `done` counts RESOLVED work — delivered or deliberately dropped — the same
 * measure `phaseDerivedStatus` uses to call a phase done. This is what keeps the
 * two honest: the bar reaches 100% exactly when the phase reads "Done", and
 * never before or after. Counting only delivered work here (the old behaviour)
 * left a phase whose remaining scope was dropped showing "Done" over a bar stuck
 * below full — the same card is struck through in the list either way (CardRow),
 * so dropped work is off the to-do list and belongs in the finished count.
 */
export function phaseCardCounts(cards: PhasedCard[], phaseId: string): { total: number; done: number } {
  let total = 0;
  let done = 0;
  for (const c of cards) {
    if (c.phase !== phaseId) continue;
    total += 1;
    if (isResolved(c.status)) done += 1;
  }
  return { total, done };
}

/**
 * How far a phase's work has come, 0–100 — the progress bar. Each card CONTRIBUTES
 * its own percent: a resolved card (delivered or dropped) counts 100, an open card
 * counts its recorded `progressPct` (0 when none is set); the bar is the average.
 * With no card carrying a %, this equals the old done-count — a resolved card is
 * 100 and every other is 0, so the average is done/total×100 to the rounding.
 *
 * THE INVARIANT HOLDS (see phaseCardCounts above): the bar reaches 100% EXACTLY
 * when the phase reads Done (every card resolved, which is when phaseDerivedStatus
 * calls it done) and never before. An open card can only ever lend up to 99, and
 * the whole bar is held below 100 until the every-card-resolved case returns 100
 * outright — so a rounding edge can never show a full bar on an unfinished phase.
 * Zero when the phase has no cards — an empty phase reads 0%, not a divide by nothing.
 */
export function phaseProgressFromCards(cards: PhasedCard[], phaseId: string): number {
  const mine = cards.filter((c) => c.phase === phaseId);
  if (mine.length === 0) return 0;
  if (mine.every((c) => isResolved(c.status))) return 100;
  const sum = mine.reduce(
    (acc, c) => acc + (isResolved(c.status) ? 100 : Math.min(c.progressPct ?? 0, 99)),
    0,
  );
  return Math.min(99, Math.round(sum / mine.length));
}

/**
 * THE STATUS A PHASE EARNS FROM ITS CARDS — D1's derived half.
 *
 * Planned until a card starts, active while work is underway, done once every
 * card is resolved (delivered or dropped). An empty phase is planned — nothing
 * has begun. The PM can override this on the Plan; the override is stored on the
 * phase and, because it is shown against this derived value, can never silently
 * drift from what the board actually says.
 */
export function phaseDerivedStatus(cards: PhasedCard[], phaseId: string): PhaseStatus {
  const mine = cards.filter((c) => c.phase === phaseId);
  if (mine.length === 0) return 'planned';
  if (mine.every((c) => isResolved(c.status))) return 'done';
  if (mine.some((c) => isStarted(c.status))) return 'active';
  return 'planned';
}

/**
 * THE STATUS A PHASE ACTUALLY SHOWS — the one canonical rule the Plan, the
 * Portfolio AND the go-live handoff must all read, so no two surfaces disagree
 * about whether a phase is done. A manual override the PM set on the Plan
 * outranks the card-derived value (but a stale stored "planned" never does, so
 * an un-touched phase still tracks its cards). On a board that tracks by status
 * column rather than the phase field, every phase derives 'planned' from its
 * zero cards — so the PM's "done" override is the ONLY signal there is, and the
 * handoff must honour it rather than read the board as forever unfinished.
 */
export function phaseShownStatus(phase: { id: string; status: PhaseStatus }, cards: PhasedCard[]): PhaseStatus {
  const derived = phaseDerivedStatus(cards, phase.id);
  return phase.status !== 'planned' && phase.status !== derived ? phase.status : derived;
}

export interface OpenPhaseSummary {
  /** The earliest delivery phase not yet done — the one the handoff names. */
  name: string;
  /** Its derived status is 'active' (work underway) vs 'planned' (not started). */
  started: boolean;
  /** Cards TAGGED TO THIS PHASE — may legitimately be 0 on a board that tracks by
   *  status column rather than the phase field. The handoff NEVER reads the board-
   *  wide carryover off this; it reads it off the committed-open scope, so an
   *  untagged-but-open board can't read "0 of 0 done" beside "6 cards carry over". */
  total: number;
  done: number;
  pct: number;
  /** How many live phases are not done — so the caller says "the last phase" only
   *  when it truly is the last, never "the only phase" when several are open. */
  remaining: number;
  phasesTotal: number;
}

/**
 * THE PHASE A GO-LIVE HANDOFF NAMES when it fires with delivery still unfinished —
 * the earliest live phase not yet done, with the honest counts around it. Pulled
 * out of HandoffPrompt so the "which phase, how far, how many left" reading is one
 * tested function, not inline JSX: the bug it guards against is a phase with no
 * tagged cards reading "0 of 0 done · the only phase" beside a board-wide carryover.
 */
export function openPhaseSummary(
  phases: { id: string; name: string; status: PhaseStatus }[],
  cards: PhasedCard[],
): OpenPhaseSummary | null {
  // The SHOWN status (override ?? derived) — the same rule the Plan and Portfolio
  // read, so the handoff can never say "phase open" about a phase they call done.
  const shown = phases.map((ph) => ({ id: ph.id, name: ph.name, status: phaseShownStatus(ph, cards) }));
  const remaining = shown.filter((ph) => ph.status !== 'done');
  const first = remaining[0];
  if (!first) return null;
  const { total, done } = phaseCardCounts(cards, first.id);
  return {
    name: first.name,
    started: first.status === 'active',
    total,
    done,
    pct: phaseProgressFromCards(cards, first.id),
    remaining: remaining.length,
    phasesTotal: shown.length,
  };
}
